import BaseModel from 'volta/models/base-model';
import { _normalize, collectionCommand, resourceCommand as command } from 'volta/utils/api';

import { attr } from '@ember-data/model';
import { isPresent } from '@ember/utils';
import { Type } from '@warp-drive/core-types/symbols';

import type {
  ICreateScheduleCmd,
  IUpdateScheduleCmd,
  TLambdaRunnerArgs,
  TScheduleArgs
} from 'volta/models/types/schedule';

export const ScheduleResourceName = {
  singular: 'schedule',
  plural: 'schedules'
};

export enum ScheduleCommands {
  CreateSchedule = 'CreateSchedule',
  UpdateSchedule = 'UpdateSchedule',
  LaunchSchedule = 'LaunchSchedule',
  DeleteSchedule = 'DeleteSchedule'
}

export type TScheduleGroup = 'Planning' | 'Export' | 'Import' | 'Custom' | 'Forecast';
export type TScheduleService =
  | 'PlanningCalculation'
  | 'RuleEngine'
  | 'OrderExport'
  | 'ImportConnector'
  | 'ForecastProject'
  | 'LambdaRunner';

export default class Schedule extends BaseModel {
  // Collection commands

  static createSchedule = collectionCommand<ICreateScheduleCmd, Schedule, typeof Schedule>(
    ScheduleCommands.CreateSchedule
  );

  // Instance commands

  updateSchedule = command<IUpdateScheduleCmd, Schedule>(ScheduleCommands.UpdateSchedule);
  launchSchedule = command<undefined, Schedule>(ScheduleCommands.LaunchSchedule);
  deleteSchedule = command<undefined, Schedule>(ScheduleCommands.DeleteSchedule);

  // Attributes

  @attr('string') group!: TScheduleGroup;
  @attr('string') service!: TScheduleService;
  @attr('string') cron?: string;
  @attr('string') timezone?: string;
  @attr('string') description!: string;
  @attr('string') calendar?: string;
  @attr('date') nextTrigger?: Date;
  @attr('boolean') enabled!: boolean;
  @attr('string') context?: string;

  @attr() arg?: TScheduleArgs;

  // Methods

  toCreateCmd() {
    return this.toUpdateCmd() as ICreateScheduleCmd;
  }

  toUpdateCmd(): IUpdateScheduleCmd {
    const arg = this.arg as TLambdaRunnerArgs;
    if (arg?.['payload']) {
      arg.payload = typeof arg.payload === 'string' ? JSON.parse(arg.payload) : arg.payload;
    }

    return {
      group: this.group,
      service: this.service,
      cron: this.cron,
      timezone: this.timezone,
      description: this.description,
      calendar: this.calendar,
      enabled: this.enabled,
      context: isPresent(this.context) ? this.context : null,
      arg: arg as TScheduleArgs
    };
  }

  [Type] = 'schedule' as const;
}
